import { lazy, memo, Suspense, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import {
  StyledApp,
  StyledLoader,
  StyledMapSurveyContainer,
} from "../App-styled";

import { useTranslation } from "react-i18next";

import { isWidgetDisplayed } from "../../../esri/widgets/expandUtils";
import { EMBED_VARIANTS } from "../../../utils/helper";
import Dashboard from "../../Dashboard/Dashboard";
import DesktopHeader from "../../Header/DesktopHeader";
import ManualWidget from "../../ManualWidgets/ManualWidget";
import MapDisclaimer from "../../MapDisclaimer/MapDisclaimer";

const MapContainer = lazy(() => import("../../MapContainer"));
const Reports = lazy(() => import("../../Report/new/Reports"));
const Sidebar = lazy(() => import("./Sidebar"));
const FeatureTableWrapper = lazy(() =>
  import("../../FeatureTable/FeatureTableWrapper")
);

const DesktopApp = ({
  config,
  openSnackbar,
  activeLanguage,
  setActiveLanguage,
}) => {
  const { t } = useTranslation("common");

  const view = useSelector((store) => store.mapViewReducer.view);
  const mapReloadCount = useSelector(
    (store) => store.mapViewReducer.mapReloadCount
  );
  const printWidget = useSelector((store) => store.printWidget);
  const cycleManagerReducer = useSelector((store) => store.cycleManagerReducer);
  const activeModule = useSelector((store) => store.activeModule);
  const editorDrawActive = useSelector(
    (store) => store.dashboard.editorDrawActive
  );

  const ref = useRef();
  // useCycleManager(config);

  const mapContainerClassName = useMemo(() => {
    if (printWidget.open && !printWidget.isLoading) {
      return "print-enabled";
    } else if (cycleManagerReducer.open) {
      return "hide-all-widgets";
    } else if (editorDrawActive) {
      return "editor-draw-active";
    } else {
      return "";
    }
  }, [
    cycleManagerReducer.open,
    printWidget.open,
    printWidget.isLoading,
    editorDrawActive,
  ]);

  const featureTableActive = useMemo(() => {
    return isWidgetDisplayed(config, "FeatureTable");
  }, [config]);

  const title = useMemo(() => {
    return t("ops." + config.id + ".title", config.title);
  }, [config, t]);

  const isAppEmbed = useMemo(
    () => config?.embed === EMBED_VARIANTS.APP,
    [config]
  );

  return (
    <StyledApp data-testid="app-main">
      <Suspense>
        {view && (
          <DesktopHeader
            key={mapReloadCount + "desktop"}
            t={t}
            config={config}
            view={view}
            setActiveLanguage={setActiveLanguage}
            openSnackbar={openSnackbar}
          />
        )}
      </Suspense>
      <Suspense>
        {view && (
          <Reports
            config={config}
            t={t}
            openSnackbar={openSnackbar}
            activeLanguage={activeLanguage}
            key={mapReloadCount + "report"}
          />
        )}
      </Suspense>
      <Suspense>
        <Sidebar config={config} />
      </Suspense>

      <StyledMapSurveyContainer
        ref={ref}
        id="mapContainer"
        className={mapContainerClassName}
        style={
          false
            ? {
                boxShadow:
                  "0px 0px 0px 1px rgba(64, 87, 109, .04), 0px 6px 20px -4px rgba(64, 87, 109, .3)",
                borderRadius: "15px",
                border: "none",
                overflow: "hidden",
              }
            : {}
        }
      >
        <Suspense fallback={<StyledLoader />}>
          <MapContainer
            id="map-container"
            activeModule={activeModule}
            activeLanguage={activeLanguage}
            setActiveLanguage={setActiveLanguage}
          />
        </Suspense>
        {view && !window.isSmall && (
          <ManualWidget key={mapReloadCount + "manual"} config={config} t={t} />
        )}
        {view && config && <Dashboard key={mapReloadCount + "dashboard"} />}
      </StyledMapSurveyContainer>
      <Suspense>
        {featureTableActive && view && config && (
          <FeatureTableWrapper
            key={mapReloadCount + "feature-table"}
            openSnackbar={openSnackbar}
          />
        )}
      </Suspense>
      {!isAppEmbed && <MapDisclaimer config={config} />}
      <meta property="og:title" content={title} />
    </StyledApp>
  );
};

export default memo(DesktopApp);
